<script>
    import httpService from '@/services/core/http.service';
    import modalService from '@/services/core/modal.service';
    import AdminGrid from '@/components/shared/AdminGrid';
    import { DxColumn, DxItem } from 'devextreme-vue/data-grid';
    import IgnoredEntityModal from '@/components/modals/company/IgnoredEntityModal';
    import ConfirmModal from '@/components/modals/ConfirmModal';
    import FreshbooksIdSearchModal from '@/components/modals/billingHistory/FreshbooksIdSearchModal';
    import InvoiceProfileSearchModal from '@/components/modals/billingHistory/InvoiceProfileSearchModal';
    import FreshbooksClientInvoicesModal from '@/components/modals/billingHistory/FreshbooksClientInvoicesModal';
    import utilsService from '@/services/core/utils.service';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import DxTagBox from 'devextreme-vue/tag-box';
    import moment from 'moment';

    export default {
        components: {
            AdminGrid,
            DxColumn,
            DxItem,
            ButtonSpinner,
            DxTagBox
        },
        data() {
            return {
                company: {},
                isLoading: 0,
                isLoadingData: 0,
                isSaving: 0,
                billingHistoryDs: httpService.getDxDataSource(`companies/${this.$route.params.companyId}/company-billing-history/grid`),
                entityHistoryDs: httpService.getDxDataSource(`companies/${this.$route.params.companyId}/billable-item-history/grid`),
                ignoredEntitiesDs: httpService.getDxDataSource(`companies/${this.$route.params.companyId}/billable-ignored-items/grid`),
                tabs: ['Config', 'Billing History', 'Entity History', 'Ignored Entities'],
                selectedTab: 'Config',
                billingSettings: {},
                frequencyTypeOptions: [
                    { id: 1, name: 'Monthly' },
                    { id: 2, name: 'Annual' }
                ],
                assetTypeOptions: [
                    { id: 1, name: 'Battery' },
                    { id: 2, name: 'Well' },
                    { id: 3, name: 'User' }
                ],
                assetPriceTypeOptions: [
                    { id: 1, name: 'All' },
                    { id: 2, name: 'Actives/Deactivated' },
                    { id: 3, name: 'Only Actives' }
                ],
                entityOptions: [],
                wellTypeOptions: [
                    { id: 0, name: 'Transferred' },
                    { id: 1, name: 'Injection Well' },
                    { id: 2, name: 'Salt Water Disposal' },
                    { id: 3, name: 'Source Well' },
                    { id: 4, name: 'Water Meter Producer' },
                    { id: 5, name: 'Producer' }
                ],
                userTypeOptions: [
                    { id: 4, name: 'Pumper' },
                    { id: 3, name: 'Exec' },
                    { id: 5, name: 'Guest' }
                ],
                batteryTypeOptions: [
                    { id: 1, name: 'Battery' },
                    { id: 2, name: 'Collection Point' }
                ],
                wellHistoryFilesTypes: [
                    { id: null, name: '-' },
                    { id: 1, name: 'All' },
                    { id: 2, name: 'Only Actives' }
                ]
            };
        },
        computed: {
            freshbooksIcon() {
                return this.billingSettings.freshbooksId ? 'bi bi-trash' : 'bi bi-search';
            },
            invoiceProfileIcon() {
                return this.billingSettings.invoiceProfileId ? 'bi bi-trash' : 'bi bi-search';
            },
            showUpdateRecurringTemplate() {
                return this.billingSettings?.freshbooksId && this.billingSettings?.autoBilling && this.$route.params.companyId == 299;
            },
            showApplyProcessingFee() {
                // Base-company-only test button; the fee reconciliation is independent of auto-billing.
                return this.billingSettings?.freshbooksId && this.$route.params.companyId == 299;
            }
        },
        methods: {
            dateFormat(cellInfo, item) {
                let tz = 'America/New_York';

                switch (item.data.timeZone) {
                    // EST Eastern Standard Time
                    case -300:
                        tz = 'America/New_York';
                        break;
                    // CST Central Standard Time
                    case -360:
                        tz = 'America/Chicago';
                        break;
                    // MST Mountain Standard Time
                    case -420:
                        tz = 'America/Denver';
                        break;
                    // PST "Pacific Standard Time"
                    case -480:
                        tz = 'America/Los_Angeles';
                        break;
                }

                const local = item.data.date.toLocaleString('en-US', { timeZone: tz });
                const formated = moment(local).format('DD/MM/YYYY HH:mm');

                cellInfo.innerHTML = `<span>${formated}</span>`;
            },
            searchFreshbooksId() {
                if (this.billingSettings.freshbooksId) {
                    this.billingSettings.freshbooksId = null;
                    return;
                }

                modalService.show(FreshbooksIdSearchModal, {
                    onSelect: selectedId => {
                        this.billingSettings.freshbooksId = selectedId;
                    }
                });
            },
            searchInvoiceProfileId() {
                if (this.billingSettings.invoiceProfileId) {
                    this.billingSettings.invoiceProfileId = null;
                    return;
                }

                modalService.show(InvoiceProfileSearchModal, {
                    companyId: this.$route.params.companyId,
                    onSelect: selectedId => {
                        this.billingSettings.invoiceProfileId = selectedId;
                    }
                });
            },
            showInvoiceProfile() {
                window.open(`https://my.freshbooks.com/#/recurring-template/${this.billingSettings.invoiceProfileId}/show`, '_blank');
            },
            showLastInvoices() {
                modalService.show(FreshbooksClientInvoicesModal, {
                    freshbooksClientId: this.billingSettings.freshbooksId
                });
            },
            loadCompany() {
                this.isLoadingData++;
                const url = `companies/${this.$route.params.companyId}`;
                httpService
                    .get(url)
                    .then(data => {
                        this.company = data;
                    })
                    .finally(() => this.isLoadingData--);
            },
            loadConfig() {
                this.isLoadingData++;
                const url = `companies/${this.$route.params.companyId}/billing-settings`;
                httpService
                    .get(url)
                    .then(data => {
                        this.loadEntityOptions(data.activeProductionPointType);
                        this.billingSettings = data;
                    })
                    .finally(() => this.isLoadingData--);
            },
            saveConfig() {
                const url = `companies/${this.$route.params.companyId}/billing-settings`;
                var companyDto = utilsService.toJS(this.billingSettings);

                if (companyDto.assetPriceType == 2 && companyDto.inactiveAssetPrice <= 0) {
                    utilsService.showToast('You must enter a valid value for inactive asset price', 'danger');
                    return;
                }

                this.isLoading++;
                httpService
                    .post(url, companyDto)
                    .then(() => {
                        utilsService.showToast('config was saved');
                    })
                    .finally(() => this.isLoading--);
            },
            loadEntityOptions(value, showWarning = false) {
                if (showWarning) utilsService.showToast('Update Freshbooks invoice recurring template', 'warning');

                if (this.$refs['entityTypes']) this.$refs['entityTypes'].instance.reset();

                this.billingSettings.allowedEntities = [];
                this.entityOptions = [];

                switch (parseInt(value)) {
                    case 1:
                        this.entityOptions = this.batteryTypeOptions;
                        break;
                    case 2:
                        this.entityOptions = this.wellTypeOptions;
                        break;
                    case 3:
                        this.entityOptions = this.userTypeOptions;
                        break;
                }
            },
            add() {
                modalService.show(IgnoredEntityModal, {
                    item: { companyId: this.$route.params.companyId },
                    onSuccess: () => {
                        this.$refs['ignoredEntitiesGrid'].refresh();
                    }
                });
            },
            remove(item) {
                modalService.show(ConfirmModal, {
                    title: 'Delete Entity',
                    msg: `Are you sure you want to DELETE entity ${item.entityName}?`,
                    btnTextOk: 'Delete',
                    onSuccess: () => {
                        const url = `companies/${item.companyId}/billable-ignored-item/${item.entityId}/delete`;
                        httpService.delete(url).then(() => {
                            this.$refs['ignoredEntitiesGrid'].refresh();
                        });
                    }
                });
            },
            downloadWellSummary() {
                httpService.downloadReport(`companies/${this.$route.params.companyId}/billable-items/download`).then();
            },
            downloadReport(id) {
                httpService.downloadReport(`company-billing-history/${id}/report/${this.$route.params.companyId}`).then();
            },
            viewInvoiceProfile() {
                if (this.billingSettings.freshbooksId) {
                    window.open(`https://my.freshbooks.com/#/client/${this.billingSettings.freshbooksId}/recurring-templates`, '_blank');
                }
            },
            updateInvoiceTemplate() {
                const url = `company-billing-history/${this.$route.params.companyId}/invoice-profile`;

                this.isSaving++;
                httpService
                    .post(url)
                    .then(() => {
                        utilsService.showToast('Invoice profile was updated');
                    })
                    .finally(() => this.isSaving--);
            },
            applyProcessingFee() {
                const url = `company-billing-history/${this.$route.params.companyId}/ensure-processing-fee`;

                this.isSaving++;
                httpService
                    .post(url)
                    .then(result => {
                        const count = result?.fixedCount ?? 0;
                        utilsService.showToast(`Processing fee applied to ${count} invoice profile(s)`);
                    })
                    .finally(() => this.isSaving--);
            }
        },
        mounted() {
            this.loadConfig();
            this.loadCompany();
        }
    };
</script>

<template>
    <div class="main-content-header">
        <h3>
            <i class="bi bi-receipt"></i>
            {{ company.name }}
        </h3>
    </div>
    <div class="main-content-body d-flex justify-content-center my-2" v-if="isLoadingData">
        <ButtonSpinner :isLoading="isLoadingData" />
    </div>
    <div class="main-content-body" v-if="!isLoadingData">
        <ul class="nav nav-tabs">
            <li class="nav-item" v-for="tab in tabs" :key="tab" @click="selectedTab = tab">
                <a class="nav-link text-upper" :class="{ active: tab == selectedTab }" href="javascript:;">{{ tab }}</a>
            </li>
        </ul>
        <div v-if="selectedTab == 'Config'">
            <form class="gsb-form m-5" @submit.prevent="saveConfig">
                <fieldset>
                    <div class="row">
                        <div class="col-sm-6">
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Freshbooks Id</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm">
                                        <input
                                            type="number"
                                            v-model="billingSettings.freshbooksId"
                                            class="form-control form-control-sm"
                                            required
                                            readonly
                                        />
                                        <button
                                            class="btn btn-primary"
                                            type="button"
                                            title="Search Freshbooks Id"
                                            @click="searchFreshbooksId"
                                        >
                                            <i :class="freshbooksIcon"></i>
                                        </button>
                                        <button
                                            v-if="billingSettings.freshbooksId"
                                            class="btn btn-outline-secondary"
                                            type="button"
                                            title="View Last Invoices"
                                            @click="showLastInvoices"
                                        >
                                            <i class="bi bi-card-list"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Invoice Profile Id</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm">
                                        <input
                                            type="number"
                                            v-model="billingSettings.invoiceProfileId"
                                            class="form-control form-control-sm"
                                            readonly
                                        />
                                        <button
                                            class="btn btn-primary"
                                            type="button"
                                            title="Select Invoice Profile"
                                            @click="searchInvoiceProfileId"
                                        >
                                            <i :class="invoiceProfileIcon"></i>
                                        </button>
                                        <button
                                            v-if="billingSettings.invoiceProfileId"
                                            class="btn btn-outline-secondary"
                                            type="button"
                                            title="View Invoice Profile"
                                            @click="showInvoiceProfile"
                                        >
                                            <i class="bi bi-file-earmark-ruled"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Frequency</label>
                                <div class="col-sm-5">
                                    <select class="form-select" v-model="billingSettings.frequencyType" required>
                                        <option>Please select one</option>
                                        <option
                                            v-for="frequencyType in frequencyTypeOptions"
                                            :value="frequencyType.id"
                                            :key="frequencyType.id"
                                        >
                                            {{ frequencyType.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Active Production Point</label>
                                <div class="col-sm-5">
                                    <select
                                        class="form-select"
                                        v-model="billingSettings.activeProductionPointType"
                                        @change="loadEntityOptions($event.target.value, true)"
                                        required
                                    >
                                        <option>Please select one</option>
                                        <option v-for="assetType in assetTypeOptions" :value="assetType.id" :key="assetType.id">
                                            {{ assetType.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Entity Type</label>
                                <div class="col-sm-5">
                                    <DxTagBox
                                        v-if="entityOptions.length > 0"
                                        v-model="billingSettings.allowedEntities"
                                        :items="entityOptions"
                                        :show-selection-controls="true"
                                        value-expr="id"
                                        display-expr="name"
                                        ref="entityTypes"
                                    />
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Asset Price Type</label>
                                <div class="col-sm-5">
                                    <select class="form-select" v-model="billingSettings.assetPriceType" required>
                                        <option>Please select one</option>
                                        <option v-for="assetType in assetPriceTypeOptions" :value="assetType.id" :key="assetType.id">
                                            {{ assetType.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Active Asset Price</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm" title="Active asset price">
                                        <span class="input-group-text">$</span>
                                        <input
                                            type="number"
                                            step=".01"
                                            v-model="billingSettings.activeAssetPrice"
                                            class="form-control form-control-sm"
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Inactive Asset Price</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm" title="Inactive asset price">
                                        <span class="input-group-text">$</span>
                                        <input
                                            type="number"
                                            step=".01"
                                            v-model="billingSettings.inactiveAssetPrice"
                                            class="form-control form-control-sm"
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Ignored Production Points</label>
                                <div class="col-sm-5">
                                    <input
                                        type="number"
                                        min="0"
                                        v-model="billingSettings.ignoredProductionPoints"
                                        class="form-control form-control-sm"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Discounted Asset Count</label>
                                <div class="col-sm-5">
                                    <input
                                        type="number"
                                        min="0"
                                        v-model="billingSettings.discountedAssetCount"
                                        class="form-control form-control-sm"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Min Service Fee</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm" title="Minimum service fee">
                                        <span class="input-group-text">$</span>
                                        <input
                                            type="number"
                                            step=".01"
                                            v-model="billingSettings.minServiceFee"
                                            class="form-control form-control-sm"
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Payment processing fee</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm" title="Payment processing fee">
                                        <span class="input-group-text">%</span>
                                        <input
                                            type="number"
                                            step=".01"
                                            v-model="billingSettings.paymentProcessingFee"
                                            class="form-control form-control-sm"
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Well History Files</label>
                                <div class="col-sm-5">
                                    <select class="form-select" v-model="billingSettings.wellHistoryFilesType" required>
                                        <option>Please select one</option>
                                        <option v-for="option in wellHistoryFilesTypes" :value="option.id" :key="option.id">
                                            {{ option.name }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="row g-0 mb-2" v-if="billingSettings.wellHistoryFilesType > 0">
                                <label class="col-sm-3 col-form-label lh-1">Well History Files Price</label>
                                <div class="col-sm-5">
                                    <div class="input-group input-group-sm" title="Well history files price">
                                        <span class="input-group-text">$</span>
                                        <input
                                            type="number"
                                            step=".01"
                                            v-model="billingSettings.wellHistoryFilesPrice"
                                            class="form-control form-control-sm"
                                            placeholder="0.00"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Auto-billing</label>
                                <div class="col-sm-5">
                                    <input
                                        class="form-check-input"
                                        id="allowSensaphone"
                                        v-model="billingSettings.autoBilling"
                                        type="checkbox"
                                    />
                                    <ButtonSpinner
                                        :isLoading="isSaving"
                                        text="Update Recurring Template"
                                        @click="updateInvoiceTemplate"
                                        v-if="showUpdateRecurringTemplate"
                                    />
                                    <ButtonSpinner
                                        :isLoading="isSaving"
                                        text="Apply Processing Fee"
                                        @click="applyProcessingFee"
                                        v-if="showApplyProcessingFee"
                                    />
                                </div>
                            </div>
                            <div class="row g-0 mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Remarks</label>
                                <div class="col-sm-5">
                                    <textarea v-model="billingSettings.remarks" class="form-control form-control-sm" />
                                </div>
                            </div>
                        </div>
                        <div class="col-sm-6">
                            <fieldset>
                                <b class="fieldset-title">Addons</b>
                            </fieldset>
                            <div class="row mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Public API Enabled</label>
                                <div class="col-sm-5">
                                    <input class="form-check-input" disabled v-model="company.publicApiEnabled" type="checkbox" />
                                </div>
                            </div>
                            <div class="row mb-2" v-if="company.publicApiEnabled">
                                <label class="col-sm-3 col-form-label lh-1">Public API readonly</label>
                                <div class="col-sm-5">
                                    <input class="form-check-input" disabled v-model="company.publicApiReadonly" type="checkbox" />
                                </div>
                            </div>
                            <div class="row mb-2" v-if="company.publicApiEnabled">
                                <label class="col-sm-3 col-form-label lh-1">Public API request limit</label>
                                <div class="col-sm-5">
                                    <input class="form-check-input" disabled v-model="company.publicApiRequestLimit" type="checkbox" />
                                </div>
                            </div>
                            <div class="row mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Allocation Sensaphone</label>
                                <div class="col-sm-5">
                                    <input
                                        class="form-check-input"
                                        disabled
                                        id="allowSensaphone"
                                        v-model="company.allowSensaphone"
                                        type="checkbox"
                                    />
                                </div>
                            </div>
                            <div class="row mb-2">
                                <label class="col-sm-3 col-form-label lh-1">Tiny Pumper Enabled</label>
                                <div class="col-sm-5">
                                    <input
                                        class="form-check-input"
                                        disabled
                                        id="allowSensaphone"
                                        v-model="company.enableTinyPumper"
                                        type="checkbox"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
                <div class="d-flex justify-content-between my-2">
                    <ButtonSpinner :isLoading="isLoading" />
                </div>
            </form>
        </div>
        <div v-if="selectedTab == 'Billing History'">
            <AdminGrid :data-source="billingHistoryDs" storage-key="billingHistoryGrid" :showActions="true">
                <DxColumn data-field="id" data-type="number" sort-order="desc" />
                <DxColumn data-field="createdOn" caption="Created On" data-type="date" />
                <DxColumn data-field="start" caption="From" data-type="date" />
                <DxColumn data-field="end" caption="To" data-type="date" />
                <DxColumn
                    data-field="detail.totalActiveProductionPoints"
                    caption="Total P.P"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.activeProductionPoints"
                    caption="P.P"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn data-field="detail.ignoredProductionPoints" caption="Ignored" data-type="number" />
                <DxColumn
                    data-field="detail.inactiveProductionPoints"
                    caption="P.P"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn data-field="detail.discountedAssetCount" caption="Discounted Asset" data-type="number" />
                <DxColumn
                    data-field="detail.activeBatteryCount"
                    caption="batteries"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveBatteryCount"
                    caption="batteries"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn
                    data-field="detail.activeCollectionPointCount"
                    caption="CP"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveCollectionPointCount"
                    caption="CP"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn data-field="detail.activeWellCount" caption="wells" data-type="number" header-cell-template="active-header" />
                <DxColumn data-field="detail.inactiveWellCount" caption="wells" data-type="number" header-cell-template="inactive-header" />
                <DxColumn
                    data-field="detail.activeBatteryAsWellCount"
                    caption="wells (battery)"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveBatteryAsWellCount"
                    caption="wells (battery)"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn data-field="detail.activeUserCount" caption="users" data-type="number" header-cell-template="active-header" />
                <DxColumn data-field="detail.inactiveUserCount" caption="users" data-type="number" header-cell-template="inactive-header" />
                <DxColumn
                    data-field="detail.activePumperUserCount"
                    caption="pumpers"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn data-field="detail.activeExecUserCount" caption="execs" data-type="number" header-cell-template="active-header" />
                <DxColumn
                    data-field="detail.activeGuestUserCount"
                    caption="guest"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.activeTransferCount"
                    caption="transfers"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveTransferCount"
                    caption="transfers"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn
                    data-field="detail.activeInjectionWellCount"
                    caption="I.W."
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveInjectionWellCount"
                    caption="I.W."
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn data-field="detail.activeSWDCount" caption="SWD" data-type="number" header-cell-template="active-header" />
                <DxColumn data-field="detail.inactiveSWDCount" caption="SWD" data-type="number" header-cell-template="inactive-header" />
                <DxColumn
                    data-field="detail.activeSourceWellCount"
                    caption="source wells"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveSourceWellCount"
                    caption="source wells"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <DxColumn
                    data-field="detail.activeProducerCount"
                    caption="producer"
                    data-type="number"
                    header-cell-template="active-header"
                />
                <DxColumn
                    data-field="detail.inactiveProducerCount"
                    caption="producer"
                    data-type="number"
                    header-cell-template="inactive-header"
                />
                <template #toolbar>
                    <DxItem location="after" widget="dxButton" :options="{ text: 'View Invoice Profile ', onClick: viewInvoiceProfile }" />
                </template>
                <template #actions="{ item }">
                    <button
                        type="button"
                        class="btn btn-link py-0 lh-1 text-success"
                        title="Download"
                        @click="downloadReport(item.data.id)"
                    >
                        <i class="bi bi-download"></i>
                    </button>
                </template>
            </AdminGrid>
        </div>
        <div v-if="selectedTab == 'Entity History'">
            <AdminGrid :data-source="entityHistoryDs" storage-key="entityHistoryGrid" :showActions="false">
                <DxColumn data-field="date" caption="Date" :cell-template="dateFormat" sort-order="desc" />
                <DxColumn data-field="user" />
                <DxColumn data-field="operation" />
                <DxColumn data-field="entityType" caption="Type" />
                <DxColumn data-field="entityName" caption="name" />
                <DxColumn data-field="reason" />
            </AdminGrid>
        </div>
        <div v-if="selectedTab == 'Ignored Entities'">
            <AdminGrid :data-source="ignoredEntitiesDs" storage-key="ignoredItems" ref="ignoredEntitiesGrid">
                <template #toolbar>
                    <DxItem location="after" widget="dxButton" :options="{ text: 'Add', onClick: add }" />
                    <DxItem location="after" widget="dxButton" :options="{ text: 'Well Summary', onClick: downloadWellSummary }" />
                </template>
                <DxColumn data-field="date" caption="Date" data-type="date" format="MM/dd/yyyy hh:mm a" />
                <DxColumn data-field="entityType" caption="Type" />
                <DxColumn data-field="entityName" caption="name" />
                <DxColumn data-field="reason" />

                <template #actions="{ item }">
                    <button type="button" class="btn btn-link py-0 lh-1 text-danger" title="Delete" @click="remove(item.data)">
                        <i class="bi bi-trash"></i>
                    </button>
                </template>
            </AdminGrid>
        </div>
    </div>
</template>
